import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EVENT_TYPES } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const LOGISTICS = [
  { key: "sound", label: "Son & Sonorisation" },
  { key: "stage", label: "Scène / Podium" },
  { key: "lights", label: "Lumières & Éclairage" },
  { key: "transport", label: "Transport de l'artiste" },
  { key: "vip_accommodation", label: "Hébergement VIP / Loge" },
  { key: "security", label: "Sécurité" },
];

const STEPS = ["Événement", "Date & Lieu", "Logistique", "Détails", "Coordonnées"];

export default function BookingModal({ artist, open, onClose }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    event_type: "",
    event_date: "",
    city: "",
    country: "",
    venue: "",
    logistics_needed: true,
    logistics: { sound: false, stage: false, lights: false, transport: false, vip_accommodation: false, security: false },
    guest_count: "",
    duration: "",
    budget_estimate: "",
    message: "",
    contact_name: user?.name || "",
    contact_email: user?.email || "",
    contact_phone: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLog = (k) => setForm((f) => ({ ...f, logistics: { ...f.logistics, [k]: !f.logistics[k] } }));

  const canNext = () => {
    if (step === 0) return !!form.event_type;
    if (step === 1) return form.event_date && form.city && form.country;
    if (step === 4) return form.contact_name && form.contact_email;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/bookings", { artist_id: artist.id, ...form });
      setDone(true);
      toast.success("Votre demande a bien été envoyée !");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setStep(0);
      setDone(false);
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-questionnaire"
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#121216] border border-[#C9A24B]/25 rounded-2xl p-6 sm:p-9"
          >
            <button
              onClick={close}
              data-testid="booking-close-btn"
              className="absolute top-5 right-5 text-[#A19E95] hover:text-white"
            >
              <X />
            </button>

            {done ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full gold-bg mx-auto flex items-center justify-center mb-6">
                  <Check className="text-black" size={30} />
                </div>
                <h3 className="font-serif text-3xl text-white mb-3">Demande envoyée</h3>
                <p className="text-[#A19E95] max-w-md mx-auto">
                  Votre demande pour <span className="text-[#C9A24B]">{artist.name}</span> a été transmise.
                  L'équipe MYSE et l'artiste étudient votre événement et reviendront vers vous avec un devis.
                </p>
                <Button onClick={close} className="gold-bg text-black font-semibold mt-8 rounded-full px-8">
                  Fermer
                </Button>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.25em] text-[#C9A24B] mb-1">Réservation</p>
                <h3 className="font-serif text-3xl text-white mb-1">{artist.name}</h3>
                <p className="text-[#A19E95] text-sm mb-6">{artist.price_display}</p>

                <div className="flex items-center gap-1.5 mb-8">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex-1">
                      <div className={`h-1 rounded-full ${i <= step ? "gold-bg" : "bg-white/10"}`} />
                      <span className={`text-[10px] mt-1 block ${i === step ? "text-[#C9A24B]" : "text-[#6E6B63]"}`}>
                        {s}
                      </span>
                    </div>
                  ))}
                </div>

                {step === 0 && (
                  <div>
                    <Label className="text-white mb-3 block">Quel type d'événement organisez-vous ?</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {EVENT_TYPES.map((t) => (
                        <button
                          key={t}
                          data-testid={`event-type-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                          onClick={() => set("event_type", t)}
                          className={`p-3 rounded-xl border text-sm transition ${
                            form.event_type === t
                              ? "border-[#C9A24B] bg-[#C9A24B]/10 text-white"
                              : "border-white/10 text-[#A19E95] hover:border-[#C9A24B]/40"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white mb-2 block">Date souhaitée</Label>
                      <Input
                        type="date"
                        data-testid="booking-date"
                        value={form.event_date}
                        onChange={(e) => set("event_date", e.target.value)}
                        className="bg-[#181820] border-white/10 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white mb-2 block">Ville</Label>
                        <Input
                          data-testid="booking-city"
                          value={form.city}
                          onChange={(e) => set("city", e.target.value)}
                          className="bg-[#181820] border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white mb-2 block">Pays</Label>
                        <Input
                          data-testid="booking-country"
                          value={form.country}
                          onChange={(e) => set("country", e.target.value)}
                          className="bg-[#181820] border-white/10 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Lieu / Contraintes d'accès (optionnel)</Label>
                      <Input
                        data-testid="booking-venue"
                        value={form.venue}
                        onChange={(e) => set("venue", e.target.value)}
                        placeholder="Château, salle, plein air..."
                        className="bg-[#181820] border-white/10 text-white"
                      />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between p-4 rounded-xl border border-white/10">
                      <div>
                        <Label className="text-white">Logistique à prévoir ?</Label>
                        <p className="text-xs text-[#A19E95] mt-1">Devons-nous organiser la technique de l'événement ?</p>
                      </div>
                      <Switch
                        data-testid="booking-logistics-switch"
                        checked={form.logistics_needed}
                        onCheckedChange={(v) => set("logistics_needed", v)}
                      />
                    </div>
                    {form.logistics_needed && (
                      <div className="grid grid-cols-2 gap-3">
                        {LOGISTICS.map((l) => (
                          <label
                            key={l.key}
                            className="flex items-center gap-3 p-3 rounded-xl border border-white/10 cursor-pointer hover:border-[#C9A24B]/40"
                          >
                            <Checkbox
                              data-testid={`logistics-${l.key}`}
                              checked={form.logistics[l.key]}
                              onCheckedChange={() => toggleLog(l.key)}
                            />
                            <span className="text-sm text-[#D8D5CD]">{l.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white mb-2 block">Nombre d'invités</Label>
                        <Input
                          data-testid="booking-guests"
                          value={form.guest_count}
                          onChange={(e) => set("guest_count", e.target.value)}
                          placeholder="ex : 150"
                          className="bg-[#181820] border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white mb-2 block">Durée de la prestation</Label>
                        <Input
                          data-testid="booking-duration"
                          value={form.duration}
                          onChange={(e) => set("duration", e.target.value)}
                          placeholder="ex : 1h30"
                          className="bg-[#181820] border-white/10 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Budget estimé</Label>
                      <Input
                        data-testid="booking-budget"
                        value={form.budget_estimate}
                        onChange={(e) => set("budget_estimate", e.target.value)}
                        placeholder="ex : 20 000 € - 30 000 €"
                        className="bg-[#181820] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Message pour l'artiste (optionnel)</Label>
                      <Textarea
                        data-testid="booking-message"
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                        rows={3}
                        className="bg-[#181820] border-white/10 text-white"
                      />
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white mb-2 block">Nom complet</Label>
                      <Input
                        data-testid="booking-name"
                        value={form.contact_name}
                        onChange={(e) => set("contact_name", e.target.value)}
                        className="bg-[#181820] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Email</Label>
                      <Input
                        type="email"
                        data-testid="booking-email"
                        value={form.contact_email}
                        onChange={(e) => set("contact_email", e.target.value)}
                        className="bg-[#181820] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Téléphone (optionnel)</Label>
                      <Input
                        data-testid="booking-phone"
                        value={form.contact_phone}
                        onChange={(e) => set("contact_phone", e.target.value)}
                        className="bg-[#181820] border-white/10 text-white"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mt-8">
                  <Button
                    variant="ghost"
                    disabled={step === 0}
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="booking-prev-btn"
                    className="text-[#A19E95] hover:text-white disabled:opacity-30"
                  >
                    <ChevronLeft size={18} /> Retour
                  </Button>
                  {step < STEPS.length - 1 ? (
                    <Button
                      disabled={!canNext()}
                      onClick={() => setStep((s) => s + 1)}
                      data-testid="booking-next-btn"
                      className="gold-bg text-black font-semibold rounded-full px-7 disabled:opacity-40"
                    >
                      Suivant <ChevronRight size={18} />
                    </Button>
                  ) : (
                    <Button
                      disabled={!canNext() || submitting}
                      onClick={submit}
                      data-testid="submit-questionnaire-btn"
                      className="gold-bg text-black font-semibold rounded-full px-7 disabled:opacity-40"
                    >
                      {submitting ? "Envoi..." : "Envoyer ma demande"}
                    </Button>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
